import React, { useState } from "react";
import { Drawer, Button } from "rsuite";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import UploadFiles from "../upload-file/uploadFiles";
import { downloadAriTemplate, uploadAriByFile } from "../../api";
import {
  extractApiErrorDescription,
  buildAriUploadErrorMessage,
} from "./utils";
import { requireLoggedInUserId } from "../../utils/sessionGuard";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import { UAL_SOURCES } from "../../constants/ualActions";
import { Can, P } from "../../rbac";
import "./upload-excel-modal.scss";

const UploadExcelModal = ({ open, onClose }) => {
  const [files, setFiles] = useState([]);
  const [resetFiles, setResetFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { selectedProperty } = usePropertyStore();

  const downloadTemplate = () => {
    downloadAriTemplate(selectedProperty.propertyId).then(
      (response) => {
        if (response.data) {
          let filename;
          try {
            filename =
              response.headers["content-disposition"]?.split("filename=")[1];
          } catch {
            // fallback
          }
          const url = globalThis.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            filename || `ari_template_${selectedProperty.propertyId}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      },
      async (rejection) => {
        const description = await extractApiErrorDescription(rejection);
        toast.error(description || "Downloading ARI Template Failed");
      }
    );
  };

  const uploadAriFile = () => {
    if (files.length === 0) {
      toast.error("No file selected to upload");
      return;
    }

    const userId = requireLoggedInUserId();
    if (!userId) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append("uploadFile", files[0]);

    uploadAriByFile(userId, formData, UAL_SOURCES.ARI_EXCEL_UPLOAD).then(
      () => {
        toast.success("Upload successful!");
        setIsUploading(false);
        setResetFiles(true);
        setFiles([]);
        onClose();
      },
      (err) => {
        setIsUploading(false);
        setResetFiles(true);
        if (err?.errors?.length > 0) {
          toast.error(buildAriUploadErrorMessage(err.errors), {
            style: { whiteSpace: "pre-line" },
          });
        } else {
          toast.error("Upload failed. Please try again.");
        }
      }
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="sm"
      className="upload-excel-drawer"
    >
      <Drawer.Header>
        <Drawer.Title>Upload Excel</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <p className="upload-excel-subtitle">
          Select a file to upload and update ARI.
        </p>
        <div className="upload-excel-body">
          <div className="upload-excel-card">
            <div className="upload-excel-property">
              <span className="label">Property</span>
              <span className="value">{selectedProperty?.propertyName}</span>
            </div>

            <UploadFiles
              className="upload-section"
              text="Drop your template here"
              butttonText="Select"
              resetFiles={resetFiles}
              setResetFiles={setResetFiles}
              onFilesSelected={setFiles}
              footerContent={
                <>
                  <span className="upload-excel-formats">
                    Supported formats: XLS, XLSX. CVS
                  </span>
                  <Can permission={P.Ari.Template.Download}>
                    <button
                      onClick={downloadTemplate}
                      className="download-template-link"
                    >
                      Download template
                    </button>
                  </Can>
                </>
              }
            />

            <Can permission={P.Ari.Template.Upload}>
              <Button
                appearance="primary"
                className="upload-file-btn"
                onClick={uploadAriFile}
                loading={isUploading}
                block
              >
                Upload File
              </Button>
            </Can>
          </div>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};

UploadExcelModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UploadExcelModal;
